import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import {
  apiClient,
  AUTH_SESSION_EXPIRED_EVENT,
  type MFAEnrollmentResponse,
  type MFAVerificationResponse,
  type SessionResponse,
} from "../api/client";

interface AuthState {
  session: SessionResponse | null;
  loading: boolean;
  notice: string | null;
  login: (username: string, password: string) => Promise<SessionResponse>;
  replacePassword: (newPassword: string) => Promise<SessionResponse>;
  beginMfaEnrollment: () => Promise<MFAEnrollmentResponse>;
  verifyMfa: (code: string) => Promise<MFAVerificationResponse>;
  changePassword: (payload: {
    current_password: string;
    new_password: string;
    step_up_code?: string;
  }) => Promise<SessionResponse>;
  recoverMfa: (payload: {
    current_password: string;
    recovery_code: string;
  }) => Promise<SessionResponse>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    apiClient
      .getSession()
      .then((value) => current && setSession(value))
      .catch(() => current && setSession(null))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, []);

  useEffect(() => {
    const expireSession = () => {
      setSession(null);
      setNotice("登录会话已过期，请重新登录后继续。");
    };
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, expireSession);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, expireSession);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      session,
      loading,
      notice,
      login: async (username, password) => {
        setNotice(null);
        const next = await apiClient.login(username, password);
        setSession(next);
        return next;
      },
      replacePassword: async (newPassword) => {
        const next = await apiClient.replacePassword(newPassword);
        setSession(next);
        return next;
      },
      beginMfaEnrollment: () => apiClient.beginMfaEnrollment(),
      verifyMfa: async (code) => {
        const next = await apiClient.verifyMfa(code);
        setSession(next);
        return next;
      },
      changePassword: async (payload) => {
        const next = await apiClient.changeCurrentPassword(payload);
        setSession(next);
        return next;
      },
      recoverMfa: async (payload) => {
        const next = await apiClient.recoverCurrentMfa(payload);
        setSession(next);
        return next;
      },
      logout: async () => {
        await apiClient.logout();
        setSession(null);
        setNotice(null);
      },
    }),
    [loading, notice, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("认证上下文尚未初始化");
  }
  return value;
}

export function ProtectedRoute() {
  const { loading, session } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div className="route-loading">正在确认登录状态…</div>;
  }
  if (!session) {
    return <Navigate replace state={{ from: location.pathname }} to="/login" />;
  }
  if (session.next_action !== "ready") {
    if (location.pathname === "/account" && session.next_action === "verify_mfa") {
      return <Outlet />;
    }
    return <Navigate replace state={{ from: location.pathname }} to="/login" />;
  }
  return <Outlet />;
}

export function OwnerRoute() {
  const { loading, session } = useAuth();
  if (loading) return <div className="route-loading">正在确认 owner 权限…</div>;
  if (session?.role !== "owner") return <Navigate replace to="/" />;
  return <Outlet />;
}
